/**
 * The main navigation class that initialises all the controls
 * nested inside itself. Indicated by the data attribute:
 *  [data-main-nav]
 */
export default class Notification {
  /**
   * Default root identifier
   * Where notifications will be housed when no other has been specified
   */
  static defaultRoot = "[data-notifications-root]";

  /**
   * Default notification identifier
   * Used to find all notifications or existing ones that might need to be hydrated
   */
  static identifier = "[data-notification]";

  /**
   * Template Root ID
   * For any dynamic notifications to render
   * Colour classes can be applied to this element
   */
  static template = "#notification__template";

  /**
   * Content identifier
   * Where to render the content for dynamic notifications
   * Using the templateRoot as the parent to look down from
   */
  static templateContent = "[data-content]";

  /**
   * Close identifier
   * The button to attach the close listener to.
   */
  static templateClose = "[data-close]";

  /**
   * Static active class, should not change unless altered from the CSS
   * for some reason
   *
   * Used to animate the menu content targets
   */
  static activeClass = "active";

  /**
   * Static active class, should not change unless altered from the CSS
   * for some reason
   *
   * Used to animate the menu content targets
   */
  static bookmarkClass = "notification__bookmark";

  /**
   * Object of themes that are available to dynamic notifications to generate
   */
  static themes = {
    sky: "sky",
    sand: "sand",
  };

  /**
   * Construct the notification using the supplied config
   * If element is supplied in config, assume a notification already exists and we need to hydrate it with some functionality
   * Override with any config supplied
   *
   * Otherwise render a new dynamic notification
   *
   * @param {HTMLDivElement} element
   */
  constructor(config) {
    // From the supplied config, grab the properties we expect to configure the notification with
    const {
      element,
      content,
      theme,
      active,
      toFront,
      forBookmark,
      timeout,
      root,
    } = config;
    this.root = root ?? document.querySelector(Notification.defaultRoot);
    // Manage initial active state, for first time transition animations that occur for notifications that are newly rendered
    let initialActiveState;

    // If for whatever reason, no root container for notifications can be found, don't create this notification
    if (!this.root) {
      console.warn("Notification container could not be found.");
      return;
    }

    // Determine the values to assign to the notification properties
    if (element) {
      // If element supplied, use this as the root for the notification. Determine the other properties based on supplied markup
      // Heavy assumption/requirement that the markup matches the template, and config overrides markup
      this.element = element;
      this.content =
        content ??
        this.element.querySelector(Notification.templateContent).innerHTML;
      // Determine if a theme is applied, if so, assign it to the theme property
      Object.values(Notification.themes).forEach((theme) => {
        this.theme = this.element.classList.contains(theme)
          ? theme
          : Notification.themes.sky;
      });
      this.close = this.element.querySelector(Notification.templateClose);
      initialActiveState = this.element.classList.contains(
        Notification.activeClass
      );
    } else {
      // Create notification from the template and config
      this.element = document
        .querySelector(Notification.template)
        .content.cloneNode(true)
        .querySelector(Notification.identifier);
      this.content = content;
      this.theme = theme;
      this.close = this.element.querySelector(Notification.templateClose);
      initialActiveState = active;
    }

    // If for whatever reason, no element was provided and no tempalte can be found, don't create this notification
    if (!this.element) {
      console.warn("Notification element is undefined.");
      return;
    }

    // Initialise notification with config
    this.setContent(this.content);
    this.setTheme(this.theme);
    // To animate the notification from the beginning, start from an inactive state
    this.setActive(false);
    // If this notification is bookmark related, add the bookmarkClass
    this.setBookmark(forBookmark);
    // Initialise click event listener to close the notification
    this.close.addEventListener("click", () => this.setActive(false));

    // set a timeout to close "growl"
    if (timeout > 0) {
      setTimeout(() => {
        this.setActive(false);
      }, timeout);
    }

    // If notification currently doesn't exist under root, render it
    if (!this.root.contains(this.element)) {
      if (toFront) {
        // Render it in front of the other notifications
        this.root.insertBefore(this.element, this.root.firstChild);
      } else {
        // Render it normally
        this.root.appendChild(this.element);
      }
    }

    // Set active state after its appended for transition (as needed)
    // Brief timeout to CSS missing transition due to JS rendering
    setTimeout(() => {
      this.setActive(initialActiveState);
    }, 10);
  }

  /**
   * Assigns content to the notification
   * Defaults to empty
   * @param {String} value
   */
  setContent(value) {
    // Assign content, default to empty
    this.element.querySelector(Notification.templateContent).innerHTML =
      value ?? "";

    this.content = value ?? "";
  }

  /**
   * Sets the theme of the notification
   * Defaults to Sky
   * @param {String} value
   */
  setTheme(value) {
    // Remove any current theme classes
    this.element.classList.remove(...Object.values(Notification.themes));
    // Apply theme
    this.element.classList.add(
      Notification.themes[value] ?? Notification.themes.sky
    );
    this.theme = value ?? Notification.themes.sky;
  }

  /**
   * Sets whether the notification is active (visible) or not
   * Defaults to false / closed
   * @param {Boolean} value
   */
  setActive(value) {
    if (value) {
      this.element.classList.add(Notification.activeClass);
    } else {
      this.element.classList.remove(Notification.activeClass);
    }

    this.active = !!value;
  }

  /**
   * Set the Bookmark class
   * @param {Boolean} value
   */
  setBookmark(value) {
    if (value) {
      this.element.classList.add(Notification.bookmarkClass);
    } else {
      this.element.classList.remove(Notification.bookmarkClass);
    }
  }
}
